import React, { createContext, useContext, useRef, useCallback, useMemo, ReactNode } from 'react';
import type L from 'leaflet';

/**
 * MapContext
 *
 * Global context for sharing Leaflet map instances.
 * Allows overlays to lock map interactions when open.
 *
 * Usage in map component:
 * ```tsx
 * const SchoolMap = () => {
 *   const { registerMap, unregisterMap } = useMapContext();
 *
 *   useEffect(() => {
 *     if (mapRef.current) {
 *       registerMap('school-map', mapRef.current);
 *       return () => unregisterMap('school-map');
 *     }
 *   }, [mapRef.current]);
 * };
 * ```
 *
 * Usage in overlay:
 * ```tsx
 * const EntitySheet = ({ open }) => {
 *   const { getMap } = useMapContext();
 *   const map = getMap('school-map');
 *   useMapInteractionLock(open, map);
 * };
 * ```
 */

interface MapContextValue {
  registerMap: (id: string, map: L.Map) => void;
  unregisterMap: (id: string) => void;
  getMap: (id: string) => L.Map | null;
}

const MapContext = createContext<MapContextValue | null>(null);

interface MapProviderProps {
  children: ReactNode;
}

export function MapProvider({ children }: MapProviderProps) {
  const mapsRef = useRef<Map<string, L.Map>>(new Map());

  const registerMap = useCallback((id: string, map: L.Map) => {
    mapsRef.current.set(id, map);
  }, []);

  const unregisterMap = useCallback((id: string) => {
    mapsRef.current.delete(id);
  }, []);

  const getMap = useCallback((id: string): L.Map | null => {
    return mapsRef.current.get(id) || null;
  }, []);

  const value = useMemo(() => ({ registerMap, unregisterMap, getMap }), [registerMap, unregisterMap, getMap]);

  return (
    <MapContext.Provider value={value}>
      {children}
    </MapContext.Provider>
  );
}

export function useMapContext(): MapContextValue {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMapContext must be used within MapProvider');
  }
  return context;
}

/**
 * Optional hook for components that may or may not be within MapProvider
 */
export function useOptionalMapContext(): MapContextValue | null {
  return useContext(MapContext);
}
